import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Select, Tooltip } from 'antd';
import { Info, Phone } from '@phosphor-icons/react';
import Typography from '../../../components/Typography';
import COLORS from '../../../util/constants/colors';
import { ALL_RECIPIENTS, buildOwnerOptions } from '../helpers';

const { Option } = Select;
const TOOLTIP_TEXT = 'Clicking on "Organizations", a Department, or a User will include all associated emails and fax numbers listed under that category';

const ReceivedTo = ({ ownerContacts, selectedRecipients, setSelectedRecipients }) => {
  const [ownerOptions, setOwnerOptions] = useState([{
    label: ALL_RECIPIENTS,
    value: ALL_RECIPIENTS,
  }]);
  const [organizations, setOrganizations] = useState([]);
  const [departments, setDepartments] = useState({});
  const [users, setUsers] = useState({});

  useEffect(() => {
    if (!ownerContacts) return;
    const departmentOwners = ownerContacts?.Departments || {};
    const organizationOwners = ownerContacts?.Organization || [];
    const userOwners = ownerContacts?.Users || {};
    setDepartments(departmentOwners);
    setUsers(userOwners);
    setOrganizations(organizationOwners);

    setOwnerOptions(buildOwnerOptions(ownerContacts, ALL_RECIPIENTS));
  }, [ownerContacts]);

  const handleClickedRecipient = (clicked) => {
    const clickedOrganization = organizations.find((organization) => organization.value === clicked.value);
    const clickedDepartment = Object.keys(departments).find(
      (department) => departments[department].find((dept) => {
        return dept.value === clicked.value;
      }),
    );
    const clickedUser = Object.keys(users).find(
      (user) => users[user].find((value) => {
        return value === clicked.value;
      }),
    );
    if (clickedOrganization) {
      setSelectedRecipients([...selectedRecipients.filter((recipient) => recipient.value !== 'Organizations' && recipient.value !== ALL_RECIPIENTS), clicked]);
    } else if (clickedDepartment) {
      // Find the department key whose values contain clicked.value
      const departmentName = Object.keys(departments).find((key) => departments[key].some((item) => item.value === clicked.value));
      setSelectedRecipients([
        ...selectedRecipients.filter(
          (recipient) => recipient.value !== `Department: ${departmentName}`
            && recipient.value !== ALL_RECIPIENTS,
        ),
        clicked,
      ]);
    } else if (clickedUser) {
      const userName = Object.keys(users).find((key) => users[key].some((value) => value === clicked.value));
      setSelectedRecipients([
        ...selectedRecipients.filter(
          (recipient) => recipient.value !== `User: ${userName}`
            && recipient.value !== ALL_RECIPIENTS,
        ),
        clicked,
      ]);
    } else {
      setSelectedRecipients([...selectedRecipients.filter((recipient) => recipient.value !== ALL_RECIPIENTS), clicked]);
    }
  };

  const handleSelect = (clicked) => {
    const clickedAll = clicked.value === ALL_RECIPIENTS;
    const clickedAllOrganization = clicked.value === 'Organizations';
    const clickedDepartment = clicked.value.startsWith('Department:');
    const clickedUser = clicked.value.startsWith('User:');
    if (clickedAll) {
      setSelectedRecipients([clicked]);
    } else if (clickedAllOrganization) {
      // Remove ALL_RECIPIENTS and all organization numbers from selected recipients if 'Organizations' is clicked
      const newSelectedRecipients = selectedRecipients.filter(
        (recipient) => recipient.value !== ALL_RECIPIENTS && !organizations.some((organization) => organization.value === recipient.value),
      );
      newSelectedRecipients.push(clicked);
      setSelectedRecipients(newSelectedRecipients);
    } else if (clickedDepartment) {
      const dept = clicked.value.split('Department: ')[1];
      // "Department Name": [{key: "key1", value: "value1"}, {key: "key2", value: "value2"}]
      const newSelectedRecipients = selectedRecipients.filter((recipient) => recipient.value !== ALL_RECIPIENTS && !departments[dept]?.some((department) => department.value === recipient.value));
      newSelectedRecipients.push(clicked);
      setSelectedRecipients(newSelectedRecipients);
    } else if (clickedUser) {
      const user = clicked.value.split('User: ')[1];
      // "User Name" : [value1, value2, value3]
      const newSelectedRecipients = selectedRecipients.filter((recipient) => recipient.value !== ALL_RECIPIENTS && !users[user]?.some((value) => {
        return value === recipient.value;
      }));
      newSelectedRecipients.push(clicked);
      setSelectedRecipients(newSelectedRecipients);
    } else {
      handleClickedRecipient(clicked);
    }
  };

  const handleDeselect = (clicked) => {
    if (clicked.value === ALL_RECIPIENTS) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(selectedRecipients.filter((recipient) => recipient.value !== clicked.value));
    }
  };

  return (
    <div className="report-input-item received-to">
      <Typography variant="text" size="small" bold>
        <Phone size={16} weight="bold" color={COLORS.BLUE_PRIMARY_LITE} />
        &nbsp;
        Received To
        &nbsp;
        <Tooltip title={TOOLTIP_TEXT}>
          <Info size={16} weight="bold" color={COLORS.TEXT_TERTIARY_LITE} />
        </Tooltip>
      </Typography>
      <Select
        mode="multiple"
        placeholder="Select Recipients"
        onSelect={handleSelect}
        onDeselect={handleDeselect}
        value={selectedRecipients}
        allowClear
        onClear={() => setSelectedRecipients([])}
        style={{ textAlign: 'left', width: '100%' }}
        dropdownMatchSelectWidth={false}
        labelInValue
        maxTagCount={1}
      >
        {ownerOptions.map((option) => (
          <React.Fragment key={`group-${option.label}`}>
            <Option key={`header-${option.label}`} value={option.label}>
              <Typography variant="text" size="xsmall" color={COLORS.TEXT_TERTIARY_LITE}>
                {option.label}
              </Typography>
            </Option>
            {option.options?.map((childOption) => (
              <Option key={`item-${childOption.value}`} value={childOption.value}>
                <div style={{ marginLeft: '10px' }}>
                  <Typography variant="text" size="xsmall">
                    {childOption.label}
                  </Typography>
                </div>
              </Option>
            ))}
          </React.Fragment>
        ))}
      </Select>
    </div>
  );
};

ReceivedTo.propTypes = {
  ownerContacts: PropTypes.shape({
    Organization: PropTypes.array,
    Departments: PropTypes.object,
    Users: PropTypes.object,
  }),
  selectedRecipients: PropTypes.array.isRequired,
  setSelectedRecipients: PropTypes.func.isRequired,
};

ReceivedTo.defaultProps = {
  ownerContacts: null,
};

export default ReceivedTo;
